import React from 'react';
import { Tabs, TabsList, TabsTrigger } from './tabs';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './button';

interface ResponsiveTabsProps {
  value: string;
  onValueChange: (value: string) => void;
  children: React.ReactNode;
  className?: string;
}

export const ResponsiveTabs: React.FC<ResponsiveTabsProps> = ({
  value,
  onValueChange,
  children,
  className = ''
}) => {
  const tabsListRef = React.useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);

  const checkScrollButtons = () => {
    if (tabsListRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tabsListRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  const scroll = (direction: 'left' | 'right') => {
    if (tabsListRef.current) {
      const scrollAmount = 200;
      const newScrollLeft = tabsListRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
      tabsListRef.current.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
    }
  };

  React.useEffect(() => {
    checkScrollButtons();
    const tabsList = tabsListRef.current;
    if (tabsList) {
      tabsList.addEventListener('scroll', checkScrollButtons);
      window.addEventListener('resize', checkScrollButtons);
      return () => {
        tabsList.removeEventListener('scroll', checkScrollButtons);
        window.removeEventListener('resize', checkScrollButtons);
      };
    }
  }, []);

  // Separate triggers from content
  const triggers: React.ReactElement[] = [];
  const content: React.ReactElement[] = [];

  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child)) {
      if (child.type === TabsTrigger) {
        triggers.push(child);
      } else {
        content.push(child);
      }
    }
  });

  return (
    <Tabs value={value} onValueChange={onValueChange} className={className}>
      <div className="relative">
        {/* Scroll buttons for mobile */}
        <div className="hidden md:block">
          {canScrollLeft && (
            <Button
              variant="outline"
              size="sm"
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 h-8 w-8 p-0 bg-white/80 backdrop-blur-sm border-gray-200 shadow-sm"
              onClick={() => scroll('left')}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
          )}
          {canScrollRight && (
            <Button
              variant="outline"
              size="sm"
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 h-8 w-8 p-0 bg-white/80 backdrop-blur-sm border-gray-200 shadow-sm"
              onClick={() => scroll('right')}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Tabs List with horizontal scroll */}
        <div className="overflow-x-auto scrollbar-hide" ref={tabsListRef}>
          <TabsList 
            className="credential-capital-tabs grid w-full grid-cols-8 md:grid-cols-8 min-w-max md:min-w-0"
            style={{ backgroundColor: '#e2e8f0 !important' }}
          >
            {triggers.map((trigger, index) => 
              React.cloneElement(trigger, {
                key: trigger.key || trigger.props.value || index,
                className: `credential-capital-tab-trigger whitespace-nowrap text-xs md:text-sm px-2 md:px-4 ${trigger.props.className || ''}`
              })
            )}
          </TabsList>
        </div>
      </div>
      {content}
    </Tabs>
  );
}; 